import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";

import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";

import MyMeetingsView from "../ui/MyMeetingsView";
import RootStackParamList from "../../../navigation/RootStackParamList";
import { auth, db } from "../../../firebase";
import { useRooms } from "../../../hooks/useRooms";
import { useNow } from "../../../hooks/useNow";
import { useAppSelector } from "../../../redux/store";
import { Room } from "../../../types/types";
import { MAX_CAPACITY } from "../../../constants/common";
import { isRoomActive } from "../../../utils/timeUtils";

export default function MyMeetingsContainer() {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  const [currentUserId, setCurrentUserId] = useState<string | null>(
    auth.currentUser?.uid ?? null,
  );
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(
    null,
  );

  const { rooms, loading, error } = useRooms();

  useNow();
  const currentTime = useAppSelector((state) => state.time.now);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUserId(user?.uid ?? null);
    });
    return unsubscribe;
  }, []);

  const myRooms = useMemo<Room[]>(() => {
    if (!currentUserId) return [];
    return rooms.filter(
      (room) =>
        room.createdBy === currentUserId ||
        room.participants?.includes(currentUserId),
    );
  }, [rooms, currentUserId]);

  const [participantsByChannelId, setParticipantsByChannelId] = useState<
    Record<string, number>
  >({});

  const ROOM_OWNER_COUNT = 1;

  useEffect(() => {
    if (!myRooms?.length) return;

    const unsubscribeList: (() => void)[] = [];
    const seen = new Set<string>();

    myRooms.forEach((room) => {
      const channelId = room.id;
      if (!channelId || seen.has(channelId)) return;
      seen.add(channelId);

      const roomRef = doc(db, "rooms", channelId);
      const unsubscribe = onSnapshot(
        roomRef,
        (snapshot) => {
          if (!snapshot.exists()) return;

          const data = snapshot.data() as any;
          const participantsField = data?.participants;

          const count = Array.isArray(participantsField)
            ? participantsField.length
            : 0;

          setParticipantsByChannelId((prev) => {
            if (prev[channelId] === count) return prev;
            return { ...prev, [channelId]: count };
          });
        },
        (err) => {
          console.error("Room subscription error:", channelId, err);
        },
      );

      unsubscribeList.push(unsubscribe);
    });

    return () => {
      unsubscribeList.forEach((off) => off());
    };
  }, [myRooms]);

  const headerTitle = "My Meetings";
  const subtitle = "Meetings you created or joined.";

  const errorText =
    (error as any)?.message ??
    (typeof error === "string" ? error : null) ??
    null;

  const items = useMemo(() => {
    return myRooms.map((room) => {
      const channelId = room.id;

      const liveParticipants = participantsByChannelId[channelId];
      const baseCount =
        typeof liveParticipants === "number"
          ? liveParticipants
          : (room.participants?.length ?? 0);

      const participantsCount = baseCount + ROOM_OWNER_COUNT;
      const showFullVisual =
        participantsCount >= (room.maxParticipants ?? MAX_CAPACITY);
      const isActive = isRoomActive(room.openDoorAt, currentTime);

      return {
        room,
        participantsCount,
        isSelected: selectedChannelId === channelId,
        showFullVisual,
        isDisabled: !isActive,
      };
    });
  }, [myRooms, participantsByChannelId, selectedChannelId, currentTime]);

  const onPressCreateMeeting = useCallback(() => {
    navigation.navigate("Schedule");
  }, [navigation]);

  const onSelectRoom = useCallback(
    (room: Room) => {
      setSelectedChannelId(room.id);
      navigation.navigate("MeetingJoin", { room });
    },
    [navigation],
  );

  return (
    <MyMeetingsView
      title={headerTitle}
      subtitle={subtitle}
      loading={loading}
      errorText={errorText}
      emptyText="You have not created or joined any meetings yet."
      items={items}
      onSelectRoom={onSelectRoom}
      onPressCreateMeeting={onPressCreateMeeting}
    />
  );
}
